//
//  components/main/LoginForm.tsx
//  TownLIT
//
//  Created by Hossein Sakkaki on 2026-08-19.
//  Last Update by Hossein Sakkaki on 2026-08-19.
//

"use client";

import React, {
  useEffect,
  useState,
} from "react";

import Link from "next/link";

import {
  useRouter,
  useSearchParams,
} from "next/navigation";

import {
  Formik,
  Form,
  Field,
} from "formik";

import * as Yup from "yup";

import {
  login,
  setLoggedOutDueTo2FA,
  clearMessages,
} from "../../redux/auth/authSlice";

import {
  useAppDispatch,
  useAppSelector,
} from "../../redux/hooks";

import { selectAuthLoadingStatus } from "../../redux/selectors/profile/userSelectors";

import { passwordValidator } from "../../utils/validators/validators";
import { isValidInternalPath } from "../../utils/security/pathValidation";
import { logger } from "../../utils/logging/logger";

import {
  Button,
  Input,
  Alert,
} from "../common";

import AppStoreBadge from "../apps/AppStoreBadge/AppStoreBadge";

import { TOWNLIT_ROUTES } from "../../config/townlitDistribution";

import "./LoginForm.css";

const identifierValidator = Yup.string()
  .trim()
  .required("Email or username is required.")
  .min(3, "Email or username is too short.")
  .max(150, "Email or username is too long.");

const validationSchema = Yup.object({
  identifier: identifierValidator,
  password: passwordValidator,
});

type LoginErrorResult = {
  message?: string;
  fieldErrors?: Record<string, string[]>;
};

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();

  const nextPath = searchParams?.get("next");

  const loading =
    useAppSelector(selectAuthLoadingStatus);

  const [generalError, setGeneralError] =
    useState<string | null>(null);

  useEffect(() => {
    dispatch(clearMessages());
  }, [dispatch]);

  useEffect(() => {
    if (nextPath) {
      localStorage.setItem(
        "login_next_path",
        nextPath,
      );
    }
  }, [nextPath]);

  const handleLogin = async (values: {
    identifier: string;
    password: string;
  }): Promise<LoginErrorResult | null> => {
    const identifier = values.identifier.trim();
    const { password } = values;

    const resultAction = await dispatch(
      login({
        identifier,
        password,
      }),
    );

    if (login.fulfilled.match(resultAction)) {
      const {
        is_member,
        two_factor_enabled,
        is_deleted,
        user_id,
        access,
        refresh,
        email,
      } = resultAction.payload;

      if (is_deleted) {
        localStorage.setItem(
          "user_id",
          String(user_id),
        );

        localStorage.setItem(
          "access_token",
          access,
        );

        localStorage.setItem(
          "refresh_token",
          refresh,
        );

        localStorage.removeItem(
          "login_next_path",
        );

        router.replace(
          "/reactivate-account",
        );

        return null;
      }

      if (two_factor_enabled) {
        dispatch(setLoggedOutDueTo2FA());

        localStorage.setItem(
          "user_email",
          email || identifier,
        );

        router.push("/verify-2fa");

        return null;
      }

      localStorage.setItem(
        "access_token",
        access,
      );

      localStorage.setItem(
        "refresh_token",
        refresh,
      );

      localStorage.setItem(
        "is_member",
        JSON.stringify(is_member),
      );

      const storedNextPath =
        localStorage.getItem(
          "login_next_path",
        );

      const redirectTo =
        storedNextPath &&
        isValidInternalPath(storedNextPath)
          ? storedNextPath
          : "/";

      router.push(redirectTo);

      localStorage.removeItem(
        "login_next_path",
      );

      return null;
    }

    logger.warn(
      "❌ Login not fulfilled",
      {
        type: resultAction.type,
      },
    );

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("is_member");

    const payload = resultAction.payload;

    if (typeof payload === "string") {
      return {
        message: payload,
      };
    }

    if (
      typeof payload === "object" &&
      payload !== null
    ) {
      return payload as LoginErrorResult;
    }

    return {
      message:
        "Login failed. Please check your email/username and password.",
    };
  };

  return (
    <section className="login-container">
      <Link
        href="/townlit-official"
        className="login-official-link"
        title="Discover the vision, beliefs, and foundation of TownLIT"
      >
        Explore the Heart of{" "}
        <span className="official-brand-name">
          TownLIT
        </span>
      </Link>

      <p
        className="util-text-hint"
        title="Discover the vision, beliefs, and foundation of TownLIT"
      >
        Where the Lord Is There!
      </p>

      <div className="login-form-wrapper">
        <Formik
          initialValues={{
            identifier: "",
            password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (
            values,
            {
              setSubmitting,
              setErrors,
            },
          ) => {
            setGeneralError(null);

            const result =
              await handleLogin(values);

            if (result) {
              if (result.fieldErrors) {
                const formattedErrors:
                  Record<string, string> = {};

                for (
                  const key in result.fieldErrors
                ) {
                  if (
                    result.fieldErrors[key]
                      ?.length > 0
                  ) {
                    const formKey =
                      key === "email"
                        ? "identifier"
                        : key;

                    formattedErrors[
                      formKey
                    ] =
                      result.fieldErrors[
                        key
                      ][0];
                  }
                }

                setErrors(
                  formattedErrors,
                );
              } else if (
                result.message
              ) {
                setGeneralError(
                  result.message,
                );
              }
            }

            setSubmitting(false);
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
          }) => (
            <Form
              className="login-form"
              onChange={() => {
                if (generalError) {
                  setGeneralError(null);
                }
              }}
            >
              <Field
                name="identifier"
                as={Input}
                type="text"
                placeholder="Email or Username"
                autoComplete="username"
                error={
                  touched.identifier &&
                  errors.identifier
                    ? errors.identifier
                    : undefined
                }
              />

              <Input
                name="password"
                placeholder="Enter your password"
                type="password"
                autoComplete="current-password"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={
                  !isValid ||
                  isSubmitting ||
                  loading
                }
                title='"I am the gate; whoever enters through me will be saved." (John 10:9)'
              >
                {loading
                  ? "Logging in..."
                  : "Gate"}
              </Button>

              {generalError && (
                <Alert
                  type="error"
                  closable
                  onClose={() =>
                    setGeneralError(
                      null,
                    )
                  }
                >
                  {generalError} 
                </Alert>
              )}
            </Form>
          )}
        </Formik>

        <Link
          href="/forget-password"
          className="login-link-forgot"
        >
          Forgot Password?
        </Link>

        <Link
          href="/register"
          className="login-link-register"
        >
          New to TownLIT? Create an account
        </Link>

        <div className="login-app-download">
          <AppStoreBadge />
        </div>
        
      </div>
    </section>
  );
};

export default LoginForm;